import { useState } from "react";
import { Eye, Loader2, ExternalLink, CheckCircle2, MessageSquare, Clock } from "lucide-react";
import { formatEmailDate } from "./format";

const PREVIEW_BADGE = {
  pending: { label: "En attente du client", cls: "bg-amber-100 text-amber-800", icon: Clock },
  approved: { label: "Maquette approuvée", cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  changes_requested: { label: "Modifications demandées", cls: "bg-gold/20 text-gold-dark", icon: MessageSquare },
};

export const FeedbackHistory = ({ feedback, testId, light = false }) => (
  <ul className="mt-3 space-y-2" data-testid={testId}>
    {feedback.map((f) => (
      <li key={f.id} className={`rounded-xl px-3 py-2 text-xs ${light ? "bg-white/10 text-white/90" : "bg-white text-slate-700"}`}>
        <p className={`font-semibold ${f.decision === "approve" ? (light ? "text-gold" : "text-emerald-700") : light ? "text-gold-light" : "text-gold-dark"}`}>
          {f.decision === "approve" ? "Maquette approuvée" : "Modifications demandées"}
          <span className={`ml-2 font-mono text-[10px] font-normal ${light ? "text-white/50" : "text-slate-400"}`}>{formatEmailDate(f.created_at)}</span>
        </p>
        {f.comment && <p className="mt-1 whitespace-pre-wrap leading-relaxed">{f.comment}</p>}
      </li>
    ))}
  </ul>
);

export const PreviewBox = ({ lead, i, onSendPreview }) => {
  const [url, setUrl] = useState(lead.preview_url || "");
  const [sending, setSending] = useState(false);
  const badge = PREVIEW_BADGE[lead.preview_status];

  const send = async () => {
    if (sending || !url.trim()) return;
    if (!window.confirm(`Envoyer la maquette ${url.trim()} à ${lead.email} pour validation ?`)) return;
    setSending(true);
    await onSendPreview(lead.id, url.trim());
    setSending(false);
  };

  return (
    <div className="mt-4 rounded-2xl border border-navy/10 bg-white p-4" data-testid={`admin-lead-preview-${i}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-navy">
          <Eye size={12} className="text-gold-dark" /> Maquette à valider
        </p>
        {badge && (
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${badge.cls}`} data-testid={`admin-lead-preview-status-${i}`}>
            <badge.icon size={10} /> {badge.label}
          </span>
        )}
      </div>
      {lead.preview_url && (
        <a href={lead.preview_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-navy underline-offset-2 hover:text-gold-dark hover:underline" data-testid={`admin-lead-preview-link-${i}`}>
          {lead.preview_url} <ExternalLink size={10} />
        </a>
      )}
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input
          data-testid={`admin-lead-preview-url-${i}`}
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://maquette-client.preview.emergentagent.com"
          className="flex-1 rounded-full border border-navy/15 bg-cream px-4 py-2 text-xs text-navy outline-none transition-colors focus:border-gold"
        />
        <button
          data-testid={`admin-lead-send-preview-${i}`}
          onClick={send}
          disabled={sending || !url.trim() || !lead.email}
          className="flex items-center justify-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-semibold text-navy transition-colors hover:border-gold hover:text-gold-dark disabled:opacity-50"
        >
          {sending ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
          {sending ? "Envoi…" : lead.preview_sent_at ? "Renvoyer pour validation" : "Envoyer pour validation"}
        </button>
      </div>
      {lead.feedback?.length > 0 && <FeedbackHistory feedback={lead.feedback} testId={`admin-lead-feedback-${i}`} />}
    </div>
  );
};
